import React from 'react';
import { useNavigate } from 'react-router-dom';

// BSC industrial palette (Bright/Light Theme)
export const C = {
  bg:       '#F8F9FA', // Crisp, bright background
  panel:    '#FFFFFF', // Pure white for cards and headers
  panel2:   '#F1F3F5', // Soft gray for input fields and image containers
  border:   '#DDE1E5', // Clean, defined borders
  borderHi: '#CED4DA', // Focus/highlighted border
  text:     '#1A1A1A', // Dark charcoal for primary text
  muted:    '#5C6370', // Medium gray for labels and helper text
  faint:    '#868E96', // Light gray for clear buttons and secondary hints
  amber:    '#D97706', // Deep amber/orange (darkened for readability on light BG)
  green:    '#16A34A', // Rich industrial green
  teal:     '#0D9488', // Deep teal for accents
  red:      '#DC2626', // High-visibility warning red
  mono: "'JetBrains Mono', ui-monospace, monospace",
  sans: "'DM Sans', system-ui, sans-serif",
};

// ── Full-screen station shell ──────────────────────────────────────────────────
export function FormShell({ title, accent = C.amber, children }) {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: C.sans }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 22px',
        borderBottom: `1px solid ${C.border}`, background: C.panel,
      }}>
        <button onClick={() => navigate('/')} style={{
          background: 'none', border: `1px solid ${C.border}`, color: C.muted,
          borderRadius: 8, width: 36, height: 36, cursor: 'pointer',
          fontFamily: C.mono, fontSize: 18,
        }}>←</button>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: accent }} />
        <span style={{
          fontFamily: C.mono, fontSize: 13, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: C.muted,
        }}>BSC Gate · {title}</span>
      </div>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '28px 22px 80px' }}>
        {children}
      </div>
    </div>
  );
}

// ── Labelled field ──────────────────────────────────────────────────────────────
export function Field({ label, hint, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 20 }}>
      <div style={{
        fontFamily: C.mono, fontSize: 11, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: C.muted, marginBottom: 8,
      }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 12, color: C.faint, marginTop: 6 }}>{hint}</div>}
    </label>
  );
}

const baseInput = {
  width: '100%', boxSizing: 'border-box', background: C.panel2,
  border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
  padding: '14px 16px', fontSize: 16, fontFamily: C.sans, outline: 'none',
};

export function TextInput({ value, onChange, placeholder, mono, autoFocus, type = 'text', style, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} autoFocus={autoFocus}
      style={{ ...baseInput, fontFamily: mono ? C.mono : C.sans,
        letterSpacing: mono ? '0.08em' : 'normal', ...style }}
      {...rest}
    />
  );
}

export function Select({ value, onChange, options, placeholder }) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
          background: '#f4f5f6',
          color: value ? '#2d3748' : '#718096',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          padding: '16px 38px 16px 16px',
          fontFamily: C.mono, fontSize: 15,
          cursor: 'pointer',
        }}
      >
        {placeholder && (
          <option value="" disabled hidden>{placeholder}</option>
        )}
        {options.map((o) => {
          const val = typeof o === 'string' ? o : o.value;
          const lbl = typeof o === 'string' ? o : o.label;
          return (
            <option key={val} value={val} style={{ color: '#2d3748', background: '#ffffff' }}>
              {lbl}
            </option>
          );
        })}
      </select>
      <span style={{
        position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
        pointerEvents: 'none',
        color: '#718096',
        fontSize: 12,
      }}>▾</span>
    </div>
  );
}


export function TextArea({ value, onChange, placeholder }) {
  return (
    <textarea value={value ?? ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} rows={2}
      style={{ ...baseInput, resize: 'vertical', minHeight: 56 }} />
  );
}

export function SubmitButton({ children, onClick, disabled, color = C.green }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width: '100%', padding: '17px 0', borderRadius: 12,
      background: disabled ? C.panel2 : color,
      color: disabled ? C.faint : '#FFFFFF',
      border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: C.mono, fontSize: 15, fontWeight: 700,
      letterSpacing: '0.08em', textTransform: 'uppercase',
      transition: 'opacity .15s',
    }}>{children}</button>
  );
}

export function Banner({ type = 'info', children }) {
const map = {
  success: { bg: '#EBF7EE', bd: C.green, fg: '#1B5E20' }, // Soft green bg, dark green text
  error:   { bg: '#FDF2F2', bd: C.red,   fg: '#9C1A1A' }, // Soft red bg, dark red text
  info:    { bg: C.panel2,  bd: C.border,fg: C.text },
};
  const s = map[type] || map.info;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.bd}`, borderRadius: 10,
      padding: '12px 16px', color: s.fg, fontSize: 14, marginBottom: 18,
      fontFamily: C.sans,
    }}>{children}</div>
  );
}